// Auto-generated by the vibesites deploy pipeline. Do not edit.
import './index.css';
import { useEffect, useState, useMemo } from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import type { Property } from '@vibesites/schema';
import type { SiteManifest, ResolvedRegistry, PageComposition, ComponentContentSchema, TemplateContext, DesignOverridesMap, ContentOverridesMap, SlotGroupOverridesMap, CollectionBindingOverridesMap, StyleOverridesMap } from '@vibesites/runtime';
import { loadProperty, mergeSections, crossLinkPagePaths, resolveSectionOverrides, isActivePage } from '@vibesites/runtime';
import {
  VibesitesProvider,
  DefaultLayout,
  PageRenderer
} from '@vibesites/runtime/react';
import { blockCatalog } from '@vibesites/blocks';
import { registry as themeRegistry } from './registry';
import { pages as bakedPages } from './vibesites-pages';
import { tokens as baseTokens } from '@vibesites-weidner-apartment-homes/theme-theme-comfy';
import {
  designOverrides,
  contentOverrides,
  slotGroupOverrides,
  collectionBindingOverrides,
  sectionOverrides,
  styleOverrides,
} from './vibesites-overrides';
import { property as bundledProperty } from './vibesites-property-data';

const PROPERTY_ID = '6767906449167167';
const API_ENDPOINT = import.meta.env.MULTIHUB_API_URL || 'https://api.multihub.io/graphql';

// Runtime token for the live GraphQL fetch path. SECURITY: never read this
// from `import.meta.env` — Vite's `define` would substitute the value as a
// string literal into the public JS bundle, exposing the service-wide secret
// to every visitor. The only legitimate caller of the runtime fetch is an
// authenticated Multihub editor preview, which forwards the user's token via
// `?authToken=…` → `vibesites-api-token` in localStorage.
// Deployed sites bake property data into vibesites-property-data.ts.
function getRuntimeApiToken(): string | null {
  try { return window.localStorage.getItem('vibesites-api-token'); } catch { return null; }
}

const manifest: SiteManifest = {
  property_id: PROPERTY_ID,
  site_id: 'alpine',
  theme: 'weidner-apartment-homes-theme-comfy',
};

const registry = {
  ...themeRegistry,
  ...Object.fromEntries(
    Object.entries(blockCatalog).map(([id, block]) => [`block:${id}`, block.component])
  ),
} as ResolvedRegistry;

const contentSchemas: Record<string, ComponentContentSchema> = {};
for (const [slot, comp] of Object.entries(registry)) {
  const schema = typeof comp === 'function'
    ? (comp as { contentSchema?: ComponentContentSchema }).contentSchema
    : undefined;
  if (schema) contentSchemas[slot] = schema;
}

const pages: Record<string, PageComposition> = Object.fromEntries(
  Object.entries(bakedPages).filter(([, page]) => isActivePage(page))
);

// Route by the page's resolved `path`, not its alias key, so a child theme can
// override the path of a base page without renaming the alias.
const pathToAlias: Record<string, string> = Object.fromEntries(
  Object.entries(pages).map(([alias, page]) => [
    page.path.replace(/\/+$/, '') || '/',
    alias,
  ])
);

const pagePaths: string[] = crossLinkPagePaths(Object.values(pages));

function resolvePageFromPath(pathname: string): { alias: string; page: PageComposition } {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const alias = pathToAlias[normalized];
  if (alias && pages[alias]) return { alias, page: pages[alias] };
  return { alias: 'home', page: pages.home };
}

function Loading() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #e0e0e0', borderTopColor: '#333', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorDisplay({ error }: { error: string }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', padding: '2rem' }}>
      <div style={{ maxWidth: 500, background: 'white', padding: '2rem', borderRadius: 8, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Unable to Load</h1>
        <p style={{ color: '#666' }}>{error}</p>
      </div>
    </div>
  );
}

function App() {
  const [property, setProperty] = useState<Property | null>(bundledProperty);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!bundledProperty);

  const currentPath = window.location.pathname;
  const { alias, page: currentPage } = resolvePageFromPath(currentPath);

  const mergedSections = useMemo(() => {
    const pageCtx = {
      alias,
      path: currentPath,
      template: currentPage.template,
      baseAlias: currentPage.template ? alias.split('--')[0] : undefined,
    };
    const combined = resolveSectionOverrides(sectionOverrides, pageCtx);
    if (combined.length === 0) return currentPage.sections;
    return mergeSections(currentPage.sections, combined);
  }, [alias, currentPage, currentPath]);

  // Hyperlocal aliases end in the page_item_url slug. Look that up so the
  // hydrated SPA matches the SSG HTML when moving between hyperlocal pages.
  const templateContext = useMemo((): TemplateContext | undefined => {
    if (currentPage.template !== 'hyperlocal' || !property) return undefined;
    const slug = alias.includes('--') ? alias.split('--').pop() : null;
    if (!slug) return undefined;
    const queries = property.hyperlocal_queries;
    if (!Array.isArray(queries)) return undefined;
    const match = queries.find((h) => h.page_item_url === slug);
    return match ? { hyperlocal: match } : undefined;
  }, [alias, currentPage, property]);

  useEffect(() => {
    if (bundledProperty) return;
    async function fetchData() {
      try {
        const apiToken = getRuntimeApiToken();
        if (!apiToken) throw new Error('Property data is unavailable. Reload the page or open this site from the Multihub editor.');
        const data = await loadProperty({ endpoint: API_ENDPOINT, token: apiToken, propertyId: PROPERTY_ID });
        setProperty(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) return <Loading />;
  if (error || !property) return <ErrorDisplay error={error || 'Property not available'} />;

  return (
    <VibesitesProvider
      manifest={manifest}
      property={property}
      registry={registry}
      page={{ alias, path: currentPath, template: currentPage.template, baseAlias: currentPage.template ? alias.split('--')[0] : undefined, title: typeof currentPage.title === 'function' ? currentPage.title(property) : (currentPage.title || ''), description: typeof currentPage.description === 'function' ? currentPage.description(property) : (currentPage.description || '') }}
      pagePaths={pagePaths}
      baseTokens={baseTokens}
      designOverrides={designOverrides as DesignOverridesMap}
      contentOverrides={contentOverrides as ContentOverridesMap}
      slotGroupOverrides={slotGroupOverrides as SlotGroupOverridesMap}
      collectionBindingOverrides={collectionBindingOverrides as CollectionBindingOverridesMap}
      contentSchemas={contentSchemas}
      styleOverrides={styleOverrides as StyleOverridesMap}
      templateContext={templateContext}
    >
      <DefaultLayout>
        <PageRenderer key={alias} sections={mergedSections} />
      </DefaultLayout>
    </VibesitesProvider>
  );
}

const container = document.getElementById('root');
if (container) {
  if (container.children.length > 0) {
    hydrateRoot(container, <App />);
  } else {
    createRoot(container).render(<App />);
  }
}
